import React, { FC, useState, useEffect } from 'react';
import { Radio, Input } from 'antd';
import { GoogleMap, LoadScript, Autocomplete, Marker } from '@react-google-maps/api';

interface ILocationObject {
  name?: string;
  lat?: number;
  lng?: number;
  address?: string;
  addressFull?: string;
  addressCity?: string;
  addressCountry?: string;
  addressState?: string;
  postalCode?: string;
  addressRegion?: string;
  addressDistrict?: string;
}

interface ILocationInput {
  value?: ILocationObject;
  onChange?: (locationObject: ILocationObject | string) => void;
  onChangeLocation?: any;
}

const defaultValue: any = '';

export enum LocationType {
  GOOGLE = 'google',
  FREETEXT = 'freetext',
}

/**
 * Map settings
 */
const libraries = ['places'];
const centerCoordinates = {
  lat: 52.520008,
  lng: 13.404954,
};
const mapZoom = 10;

const googleMapsApiKey = 'AIzaSyDL7oFL5t03Jta0KdPZjjGnXeFarpR6cJg';

export const LocationInput: FC<ILocationInput> = ({
  // @ts-ignore
  value,
  onChange = () => {},
  onChangeLocation,
}: ILocationInput) => {
  const isValueCoordinates = value?.lat && value?.lng;
  const [locationType, setLocationType] = useState(LocationType.FREETEXT);
  const [autocompleteInstance, setAutocompleteInstance] = useState<any>();
  const [markerCoordinates, setMarkerCoordinates] = useState<any>();
  const [mapInstance, setMapInstance] = useState<any>();
  const [locationFreetext, setLocationFreetext] = useState('');

  useEffect(() => {
    if (isValueCoordinates) {
      setMarkerCoordinates({ lat: value.lat, lng: value.lng });
      setLocationType(LocationType.GOOGLE);
    }

    if (!isValueCoordinates) {
      // @ts-ignore
      setLocationFreetext(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Radio.Group
        style={{ marginBottom: 10 }}
        onChange={e => {
          setLocationType(e.target.value);
          onChangeLocation(e.target.value);
        }}
        value={locationType}
      >
        <Radio.Button value={LocationType.GOOGLE}>Standort Adresse</Radio.Button>
        <Radio.Button value={LocationType.FREETEXT}>Kein Standort</Radio.Button>
      </Radio.Group>

      {locationType === LocationType.GOOGLE && (
        <>
          {/* @ts-ignore */}
          <LoadScript libraries={libraries} googleMapsApiKey={googleMapsApiKey} language="de-DE">
            <GoogleMap
              mapContainerStyle={{
                width: '100%',
                height: '400px',
              }}
              center={markerCoordinates || centerCoordinates}
              zoom={mapZoom}
              onLoad={(map: any) => {
                setMapInstance(map);
              }}
              options={{
                disableDefaultUI: true,
              }}
            >
              <Autocomplete
                fields={['address_components', 'geometry.location', 'formatted_address']}
                restrictions={{
                  country: 'de',
                }}
                onLoad={autocomplete => setAutocompleteInstance(autocomplete)}
                onPlaceChanged={() => {
                  const place = autocompleteInstance.getPlace();

                  if (place) {
                    const location = place.geometry.location;
                    const lat = location.lat();
                    const lng = location.lng();

                    // https://developers.google.com/maps/documentation/javascript/geocoding#GeocodingAddressTypes
                    const addressCity = place.address_components.find((component: any) =>
                      component.types.includes('locality'),
                    )?.long_name;
                    const addressRegion = place.address_components.find((component: any) =>
                      component.types.includes('administrative_area_level_2'),
                    )?.long_name;
                    const addressState = place.address_components.find((component: any) =>
                      component.types.includes('administrative_area_level_1'),
                    )?.long_name;
                    const addressCountry = place.address_components.find((component: any) =>
                      component.types.includes('country'),
                    )?.long_name;
                    const addressDistrict = place.address_components.find((component: any) =>
                      component.types.includes('sublocality_level_1'),
                    )?.long_name;
                    const postalCode = place.address_components.find((component: any) =>
                      component.types.includes('postal_code'),
                    )?.long_name;

                    const addressFull = place.formatted_address;

                    onChange({
                      lat,
                      lng,
                      // @ts-ignore
                      addressCity,
                      addressState,
                      addressCountry,
                      postalCode,
                      addressFull,
                      addressRegion,
                      addressDistrict,
                    });
                    setMarkerCoordinates({
                      lat,
                      lng,
                    });

                    if (mapInstance) {
                      mapInstance.panTo({ lat, lng });
                    }
                  }
                }}
              >
                <input
                  type="text"
                  placeholder="Enter Address"
                  defaultValue={
                    value
                      ? value['addressFull'] ||
                        value['addressCity'] ||
                        value['addressState'] ||
                        value['addressRegion'] ||
                        value['addressCountry']
                      : ''
                  }
                  style={{
                    boxSizing: 'border-box',
                    border: '1px solid transparent',
                    width: '50%',
                    height: '40px',
                    padding: '0 12px',
                    borderRadius: '3px',
                    boxShadow: '0 2px 3px rgba(0, 0, 0, 0.1)',
                    fontSize: '14px',
                    outline: 'none',
                    textOverflow: 'ellipses',
                    position: 'absolute',
                    left: '50%',
                    top: 10,
                    transform: 'translateX(-50%)',
                  }}
                />
              </Autocomplete>

              {markerCoordinates && <Marker position={markerCoordinates} />}
            </GoogleMap>
          </LoadScript>
        </>
      )}

      {locationType === LocationType.FREETEXT && (
        <>
          <Input.TextArea
            onChange={e => {
              const trimmedText = e.target.value.trimLeft() as ILocationObject;
              setLocationFreetext(trimmedText as string);
              onChange(trimmedText);
            }}
            value={locationFreetext}
            autoSize={{ minRows: 5 }}
            placeholder="Beschreibungstext eingeben"
          />
        </>
      )}
    </>
  );
};
